import { CreateSportRequest, CreateSportResponse, Sport, MediaLink, CreateSportsLinkRequest } from '@/types/model';
import { apiClient, QueryParams, PaginatedResponse } from './apiClient';

export interface SportsQueryParams extends QueryParams {
  active?: boolean;
  categoryId?: string;
  year?: number;
  kidsContent?: boolean;
  adultContent?: boolean;
  genre?: string;
}

export const sportsService = {
  /**
   * Get sports with pagination, search, and filtering
   */
  async getSports(params: SportsQueryParams = {}): Promise<PaginatedResponse<Sport>> {
    return apiClient.getPaginated<Sport>('/sports', params);
  },

  /**
   * Get a single sport by ID
   */
  async getSportById(id: string): Promise<Sport> {
    return apiClient.getById<Sport>('/sports', id);
  },

  /**
   * Create a new sport
   */
  async createSport(sportData: CreateSportRequest): Promise<CreateSportResponse> {
    return apiClient.create<CreateSportRequest, CreateSportResponse>('/sports', sportData);
  },

  /**
   * Update a sport
   */
  async updateSport(id: string, sportData: Partial<CreateSportRequest>): Promise<CreateSportResponse> {
    return apiClient.update<Partial<CreateSportRequest>, CreateSportResponse>('/sports', id, sportData);
  },

  /**
   * Delete a sport
   */
  async deleteSport(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>('/sports', id);
  },

  /**
   * Toggle sport active status
   */
  async toggleSportStatus(id: string, active: boolean): Promise<CreateSportResponse> {
    return apiClient.toggleStatus<CreateSportResponse>('/sports', id, active);
  },

  // Sports Links Management

  /**
   * Get links for a sport
   */
  async getSportLinks(sportId: string): Promise<MediaLink[]> {
    return apiClient.get<MediaLink[]>(`/sports/${sportId}/links`);
  },

  /**
   * Add a link to a sport
   */
  async addSportLink(linkData: CreateSportsLinkRequest): Promise<{ success: boolean; message: string; link?: MediaLink }> {
    return apiClient.post<{ success: boolean; message: string; link?: MediaLink }>(`/sports/${linkData.sportId}/links`, linkData);
  },

  /**
   * Update a sport link
   */
  async updateSportLink(sportId: string, linkId: string, linkData: Partial<CreateSportsLinkRequest>): Promise<{ success: boolean; message: string; link?: MediaLink }> {
    return apiClient.put<{ success: boolean; message: string; link?: MediaLink }>(`/sports/${sportId}/links/${linkId}`, linkData);
  },

  /**
   * Delete a sport link
   */
  async deleteSportLink(sportId: string, linkId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(`/sports/${sportId}/links/${linkId}`);
  },

  /**
   * Toggle sport link active status
   */
  async toggleSportLinkStatus(sportId: string, linkId: string, active: boolean): Promise<{ success: boolean; message: string }> {
    return apiClient.patch<{ success: boolean; message: string }>(`/sports/${sportId}/links/${linkId}/status`, { active });
  },
};
